import { useQuery } from '@apollo/client';
import IconChevronLeft from '@apollo/icons/default/IconChevronLeft.svg';
import IconChevronRight from '@apollo/icons/default/IconChevronRight.svg';
import IconCode from '@apollo/icons/default/IconCode.svg';
import IconDiff from '@apollo/icons/default/IconDiff.svg';
import IconLink from '@apollo/icons/default/IconLink.svg';
import IconClose from '@apollo/icons/small/IconClose.svg';
import IconRocket from '@apollo/icons/small/IconRocket.svg';
import { AlertBanner, Button, IconButton, Link } from '@apollo/orbit';
import { Conversation } from '@apollo/space-kit/illustrations/Conversation';
import { NotFound } from '@apollo/space-kit/illustrations/NotFound';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link as ReactRouterLink,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';

import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { format } from 'src/lib/format';
import { getLastFromMinLengthArray } from 'src/lib/getLastFromMinLengthArray';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { catchAllRouteConfig } from 'src/lib/routeConfig/catchAllRoute';

import { GraphRef } from '../../hooks/useGraphRef';
import {
  launchesRouteConfig,
  proposalChangesRouteConfig,
  proposalEditorRouteConfig,
} from '../../routes';
import { CommentMarkdownEditor } from '../CommentMarkdownEditor/CommentMarkdownEditor';
import { useGetUsersForMentions } from '../CommentMarkdownEditor/useMentionUsers';

import { NEW_COMMENT_TEMP_ID, ProposalCommentDrawerQuery } from './shared';
import { SingleProposalComment } from './SingleProposalComment';
import { useAddProposalComment } from './useAddProposalComment';

const MAX_VISIBLE_COMMENTS = 10;

export const ProposalCommentDrawer = ({
  isOpen,
  onClose,
  className,
  graphRef,
  sourceGraphVariant,
  canQueryLaunches,
  schemaScope,
  commentThreadId,
  parentComments,
  implementedChanges,
  isStale,
}: {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  graphRef: GraphRef;
  sourceGraphVariant: string | undefined;
  canQueryLaunches: boolean;
  schemaScope: string | undefined;
  commentThreadId: string | undefined;
  parentComments: GraphQLTypes.ProposalCommentsParentCommentFragment[];
  implementedChanges: Omit<
    GraphQLTypes.ImplementedChangesFragment['implementedChanges'][0],
    'subgraph'
  >[];
  isStale: boolean;
}) => {
  const scrollToBottomRef = useRef<HTMLDivElement>(null);
  const commentEditor = useRef<{
    getValue: () => string;
    setValue: (value: string) => void;
  } | null>(null);
  const [shouldShowAllComments, setShouldShowAllComments] = useState(false);

  const handleCopy = useCallback(() => copy(window.location.href), []);
  const { linkedCoordinateName: schemaCoordinate } = useRouteParams(
    proposalChangesRouteConfig,
    proposalEditorRouteConfig,
    catchAllRouteConfig,
  );
  const implementedLaunchId = useMemo(() => {
    return implementedChanges.find((change) =>
      'coordinate' in change.diffItem
        ? change.diffItem.coordinate === schemaCoordinate
        : false,
    )?.launchId;
  }, [implementedChanges, schemaCoordinate]);

  const isEditorRoute = !!useRouteMatch(proposalEditorRouteConfig.definition);

  const { data, loading, refetch } = useQuery(ProposalCommentDrawerQuery, {
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    variables: {
      ...graphRef,
      commentId: commentThreadId ?? '',
      shouldQueryComments: !!commentThreadId,
    },
    fetchPolicy:
      // We don't want to send an actual query using the `temp-id`, we want to
      // rely on the optimistic update only in this case
      commentThreadId === NEW_COMMENT_TEMP_ID ? 'cache-only' : 'cache-first',
  });

  const graph = data?.service;
  const variant = graph?.variant;
  const proposal = variant?.proposal;

  const parentComment = proposal?.comment;
  const commentsHaveLoaded =
    !loading && parentComment?.__typename === 'ParentChangeProposalComment';
  const shouldShowMarkdownCommentEditor =
    !commentThreadId || commentsHaveLoaded;

  const usersForMentions = useGetUsersForMentions(graph);

  const {
    addComment,
    loading: addCommentLoading,
    commentErrorMessage,
    setCommentErrorMessage,
    handleAddCommentResult,
  } = useAddProposalComment({
    proposal,
    graphRef,
    users: usersForMentions.users,
    commentThreadId,
  });

  const handleSubmitComment = useCallback(
    async (getValue: () => string, setValue: (value: string) => void) => {
      if (!schemaCoordinate) {
        return;
      }

      const value = getValue();

      const [result] = await Promise.all([
        addComment({
          message: value,
          schemaScope,
          schemaCoordinate,
        }),
        setValue(''),
        setShouldShowAllComments(true),
      ]);

      handleAddCommentResult({
        result,
        onError: () => {
          setValue(value);
        },
      });
    },
    [addComment, handleAddCommentResult, schemaCoordinate, schemaScope],
  );

  const location = useLocation();

  const parentCommentsInCurrentScope = useMemo(
    () =>
      parentComments.filter((comment) => comment.schemaScope === schemaScope),
    [parentComments, schemaScope],
  );

  const currentCommentIndex = parentCommentsInCurrentScope.findIndex(
    (c) => c.id === commentThreadId,
  );

  // When we switch between comment threads or subgraphs we should re-hide the
  // comments and reset the text within the markdown editor for comments
  useEffect(() => {
    setShouldShowAllComments(false);
    setCommentErrorMessage(null);
    commentEditor.current?.setValue('');
  }, [schemaScope, schemaCoordinate, setCommentErrorMessage]);

  // When we optimistically render comments so when loading changes, the comment should
  // be visible (whereas using data would only call this when the mutation is successful)
  useEffect(() => {
    scrollToBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [addCommentLoading]);

  return (
    <div
      className={classnames(
        'z-30 h-full w-[26rem] border-l border-l-silver-dark bg-white 4xl:w-[37rem]',
        !isOpen && 'hidden',
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-b-silver-dark px-1 py-2">
        <div className="space-x-2">
          <IconButton
            aria-label="Previous Comment"
            isDisabled={currentCommentIndex <= 0}
            size="sm"
            variant="hidden"
            icon={<IconChevronLeft className="text-neutral" />}
            as={ReactRouterLink}
            to={
              isEditorRoute
                ? proposalEditorRouteConfig.locationFrom({
                    location,
                    patch: {
                      linkedCoordinateName:
                        parentCommentsInCurrentScope[currentCommentIndex - 1]
                          ?.schemaCoordinate,
                      linkedCommentId:
                        parentCommentsInCurrentScope[currentCommentIndex - 1]
                          ?.id,
                    },
                    fromRouteConfig: proposalEditorRouteConfig,
                  })
                : proposalChangesRouteConfig.locationFrom({
                    location,
                    patch: {
                      linkedCoordinateName:
                        parentCommentsInCurrentScope[currentCommentIndex - 1]
                          ?.schemaCoordinate,
                      linkedCommentId:
                        parentCommentsInCurrentScope[currentCommentIndex - 1]
                          ?.id,
                      linkedGroupName: 'unknown',
                    },
                    fromRouteConfig: proposalChangesRouteConfig,
                  })
            }
          />
          <IconButton
            aria-label="Next Comment"
            isDisabled={
              currentCommentIndex === parentCommentsInCurrentScope.length - 1
            }
            size="sm"
            variant="hidden"
            icon={<IconChevronRight />}
            as={ReactRouterLink}
            to={
              isEditorRoute
                ? proposalEditorRouteConfig.locationFrom({
                    location,
                    patch: {
                      linkedCoordinateName:
                        parentCommentsInCurrentScope[currentCommentIndex + 1]
                          ?.schemaCoordinate,
                      linkedCommentId:
                        parentCommentsInCurrentScope[currentCommentIndex + 1]
                          ?.id,
                    },
                    fromRouteConfig: proposalEditorRouteConfig,
                  })
                : proposalChangesRouteConfig.locationFrom({
                    location,
                    patch: {
                      linkedCoordinateName:
                        parentCommentsInCurrentScope[currentCommentIndex + 1]
                          ?.schemaCoordinate,
                      linkedCommentId:
                        parentCommentsInCurrentScope[currentCommentIndex + 1]
                          ?.id,
                      linkedGroupName: 'unknown',
                    },
                    fromRouteConfig: proposalChangesRouteConfig,
                  })
            }
          />
        </div>
        <div className="flex items-center space-x-1">
          {commentThreadId &&
            (isEditorRoute ? (
              <Tooltip label="View conversation in Changes" placement="top">
                <IconButton
                  size="sm"
                  variant="hidden"
                  aria-label="View conversation in Changes"
                  icon={<IconDiff />}
                  as={ReactRouterLink}
                  to={proposalChangesRouteConfig.locationFrom({
                    location,
                    patch: {
                      linkedGroupName: 'Unknown',
                    },
                    fromRouteConfig: proposalEditorRouteConfig,
                  })}
                />
              </Tooltip>
            ) : (
              <Tooltip label="View conversation in Editor" placement="top">
                <IconButton
                  size="sm"
                  variant="hidden"
                  aria-label="View conversation in Editor"
                  icon={<IconCode />}
                  as={ReactRouterLink}
                  to={proposalEditorRouteConfig.locationFrom({
                    location,
                    fromRouteConfig: proposalChangesRouteConfig,
                  })}
                />
              </Tooltip>
            ))}
          <Tooltip
            label="Copy link to comment thread"
            clickedLabel="Copied"
            placement="top"
          >
            <IconButton
              size="sm"
              variant="hidden"
              aria-label="Copy link to comment thread"
              icon={<IconLink />}
              onClick={handleCopy}
            />
          </Tooltip>
          <IconButton
            size="sm"
            variant="hidden"
            aria-label="Close comment drawer"
            icon={<IconClose />}
            onClick={onClose}
          />
        </div>
      </div>
      {implementedLaunchId && sourceGraphVariant && (
        <AlertBanner status="info">
          <IconRocket />
          <div className="mx-4">
            {canQueryLaunches ? (
              <>
                This change was implemented in launch{' '}
                <Link
                  to={launchesRouteConfig.location({
                    graphId: graphRef.graphId,
                    graphVariant: sourceGraphVariant,
                    launchId: implementedLaunchId,
                  })}
                >
                  {implementedLaunchId}
                </Link>
              </>
            ) : (
              <> This change has been implemented.</>
            )}
          </div>
        </AlertBanner>
      )}
      <div
        className={classnames(
          'relative flex min-h-0 flex-1 flex-col overflow-y-auto',
          shouldShowMarkdownCommentEditor
            ? 'h-[calc(100%-14.125rem-53px)]'
            : 'h-[calc(100%-53px)]',
          !commentsHaveLoaded ? 'items-center justify-center' : 'px-6',
        )}
      >
        {!commentThreadId ? (
          <>
            {commentErrorMessage !== null && (
              <ErrorMessage className="absolute top-3">
                {isStale
                  ? 'Your comment was not saved because this proposal is out of date, please refresh and try again.'
                  : commentErrorMessage}
              </ErrorMessage>
            )}
            <Conversation className="mb-3" />
            <h3 className="text-center text-xl font-semibold">
              Start a conversation
            </h3>
            <p className="w-[40ch] text-center text-base text-secondary">
              Write your first comment to add rationale to your changes or begin
              a discussion with your team.
            </p>
          </>
        ) : loading ? (
          <Loading />
        ) : !commentsHaveLoaded ? (
          <>
            <NotFound className="mb-3" />
            <h3 className="text-center text-xl font-semibold">
              Something went wrong
            </h3>
            <p className="mb-3 w-[40ch] text-center text-base text-secondary">
              Try refreshing this panel.
            </p>
            <Button
              onClick={() =>
                refetch({ ...graphRef, commentId: commentThreadId })
              }
            >
              Refresh
            </Button>
          </>
        ) : (
          <>
            {commentErrorMessage !== null && (
              <ErrorMessage className="sticky top-3 z-10">
                {commentErrorMessage}
              </ErrorMessage>
            )}
            <div className="my-6 w-full rounded bg-silver-light p-4">
              <div
                className="truncate border-l-2 border-primary pl-3 font-mono text-sm"
                title={parentComment.schemaCoordinate}
              >
                {parentComment.schemaCoordinate}
              </div>
            </div>
            <div className="space-y-6 pb-6">
              <SingleProposalComment
                usersForMentions={usersForMentions}
                comment={parentComment}
                commentThreadId={commentThreadId}
              />
              {parentComment.replies
                .slice(
                  0,
                  shouldShowAllComments
                    ? parentComment.replies.length
                    : MAX_VISIBLE_COMMENTS - 1,
                )
                .map((replyComment) => (
                  <SingleProposalComment
                    key={replyComment.id}
                    usersForMentions={usersForMentions}
                    comment={replyComment}
                    commentThreadId={commentThreadId}
                    loading={replyComment.id === NEW_COMMENT_TEMP_ID}
                  />
                ))}
              {!shouldShowAllComments &&
                isMinLengthArray(
                  MAX_VISIBLE_COMMENTS + 1,
                  parentComment.replies,
                ) && (
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 border-t border-t-silver-dark" />
                    <ClickableText
                      className="text-link"
                      onClick={() => setShouldShowAllComments(true)}
                    >
                      Show {parentComment.replies.length - MAX_VISIBLE_COMMENTS}{' '}
                      more{' '}
                      {format.pluralize(
                        parentComment.replies.length - MAX_VISIBLE_COMMENTS,
                        'comment',
                      )}
                    </ClickableText>
                    <div className="flex-1 border-t border-t-silver-dark" />
                  </div>
                )}
              {!shouldShowAllComments &&
                isMinLengthArray(
                  MAX_VISIBLE_COMMENTS,
                  parentComment.replies,
                ) && (
                  <SingleProposalComment
                    key={getLastFromMinLengthArray(parentComment.replies).id}
                    usersForMentions={usersForMentions}
                    comment={getLastFromMinLengthArray(parentComment.replies)}
                    commentThreadId={commentThreadId}
                    loading={
                      getLastFromMinLengthArray(parentComment.replies).id ===
                      NEW_COMMENT_TEMP_ID
                    }
                  />
                )}
            </div>
            <div ref={scrollToBottomRef} />
          </>
        )}
      </div>
      <div
        className={classnames(
          'px-4 pb-2',
          !shouldShowMarkdownCommentEditor && 'hidden',
        )}
      >
        <CommentMarkdownEditor
          usersForMentions={usersForMentions}
          // TODO(proposals): pass text of current line to this editor in suggestionText prop
          actions={({ hasComment, getValue, setValue }) => {
            if (!commentEditor.current) {
              commentEditor.current = { getValue, setValue };
            }
            return (
              <Tooltip
                disabled={!isStale}
                label="This proposal is out of date, please refresh to add a comment."
              >
                <Button
                  // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
                  // TODO(Maya) add link to synthetics test
                  data-testid="proposals_submit_add_comment_button"
                  isLoading={addCommentLoading}
                  loadingText="Commenting"
                  size="sm"
                  isDisabled={!hasComment || isStale}
                  onClick={() => handleSubmitComment(getValue, setValue)}
                >
                  Comment
                </Button>
              </Tooltip>
            );
          }}
        />
      </div>
    </div>
  );
};
